import type { CSSProperties } from 'vue'

import ImageElement from './ImageElement.vue'

import type { FlashcardSize, ImageElementInput } from '../types'
import type { ElementDefinition } from '../runtime/elementDefinition'
import { scaleWidth } from '../runtime/math'

const estimateImageBox = (input: ImageElementInput, size: FlashcardSize) => {
  if (input.fullScreenImage) {
    return { widthPx: size.width, heightPx: size.height }
  }
  const aspectRatio = typeof input.aspectRatio === 'number' ? input.aspectRatio : null
  const baseSize = typeof input.size === 'number' ? input.size : null
  if (!aspectRatio || !baseSize || aspectRatio <= 0 || baseSize <= 0) return null

  const widthPx = scaleWidth(baseSize, size)
  const heightPx = widthPx / aspectRatio
  if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || widthPx <= 0 || heightPx <= 0) {
    return null
  }
  return { widthPx, heightPx }
}

const definition: ElementDefinition<ImageElementInput> = {
  type: 'image',
  component: ImageElement,
  estimateBox: (input, size) => estimateImageBox(input, size),
  toProps: (input, ctx) => {
    if (!ctx.baseStyle || typeof input.src !== 'string') {
      return null
    }

    const style: CSSProperties = {
      ...ctx.baseStyle,
      objectFit: 'cover',
      display: 'block',
    }

    if (input.fullScreenImage) {
      style.left = '0'
      style.top = '0'
      style.width = '100%'
      style.height = '100%'
    } else {
      if (typeof input.aspectRatio !== 'number' || typeof input.size !== 'number') {
        return null
      }

      if (input.aspectRatio <= 0 || input.size <= 0) {
        return null
      }

      const widthPx = scaleWidth(input.size, ctx.size)
      const heightPx = widthPx / input.aspectRatio

      if (!Number.isFinite(widthPx) || !Number.isFinite(heightPx) || heightPx <= 0) {
        return null
      }

      style.width = `${widthPx}px`
      style.height = `${heightPx}px`
    }

    if (ctx.theme.darkMode && input.forceDarkMode) {
      style.filter = 'invert(1)'
    }

    return {
      src: input.src,
      alt: typeof input.alt === 'string' ? input.alt : '',
      style,
    }
  },
}

export default definition
